import React, { useEffect } from "react";
import { Box, Dialog, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { toMediaUrl, withImageSize } from "../api";

export default function ImageGalleryModal({
  open,
  onClose,
  title,
  images,
  index,
  setIndex,
  t = (key, fallback) => fallback || key,
}) {
  const hasImages = Array.isArray(images) && images.length > 0;

  const prev = () => {
    if (!hasImages) return;
    setIndex((i) => (i - 1 + images.length) % images.length);
  };

  const next = () => {
    if (!hasImages) return;
    setIndex((i) => (i + 1) % images.length);
  };

  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, images]);

  const current = hasImages ? images[index] : null;

  // XL for modal
  const base = current?.path ?? current?.url ?? "";
  const xlUrl = base
    ? (base.startsWith("http") ? withImageSize(base, "xl") : toMediaUrl(withImageSize(base, "xl")))
    : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
    >
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontWeight: 900, flex: 1, pl: 1, minWidth: 0, }} noWrap>
            {title || t("common.image")}
          </Typography>

          <IconButton onClick={onClose} aria-label={t("common.close")}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box
          sx={{
            mt: 1,
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            position: "relative",
            height: { xs: 320, sm: 520 },
          }}
        >
          {xlUrl ? (
            <img
              src={xlUrl}
              alt={current?.name || title || t("common.image")}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography color="text.secondary">{t("common.noImage")}</Typography>
            </Box>
          )}

          {hasImages && images.length > 1 ? (
            <>
              <IconButton
                onClick={prev}
                aria-label={t("common.previous")}
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(255,255,255,0.7)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <IconButton
                onClick={next}
                aria-label={t("common.next")}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(255,255,255,0.7)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          ) : null}
        </Box>

        {hasImages && images.length > 1 ? (
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            {index + 1} / {images.length}
          </Typography>
        ) : null}
      </Box>
    </Dialog>
  );
}
